import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

const GlobalLoaderContext = createContext({
  isLoading: false,
  showLoader: () => {},
  hideLoader: () => {},
});

export function GlobalLoaderProvider({ children }) {
  const [manualLoaderCount, setManualLoaderCount] = useState(0);
  const [suppressGlobalOverlay, setSuppressGlobalOverlay] = useState(false);

  const authLoading = useSelector((state) => state.auth?.loading ?? false);
  const employeeLoading = useSelector((state) => state.employee?.loading ?? false);
  const settingsLoading = useSelector((state) => state.settings?.loading ?? false);

  const isReduxLoading = authLoading || employeeLoading || settingsLoading;
  const isManualLoading = manualLoaderCount > 0;

  const isLoading = !suppressGlobalOverlay && (isReduxLoading || isManualLoading);

  const showLoader = useCallback(() => {
    setManualLoaderCount((count) => count + 1);
  }, []);

  const hideLoader = useCallback(() => {
    setManualLoaderCount((count) => Math.max(0, count - 1));
  }, []);

  const disableGlobalOverlay = useCallback(() => {
    setSuppressGlobalOverlay(true);
  }, []);

  const enableGlobalOverlay = useCallback(() => {
    setSuppressGlobalOverlay(false);
  }, []);

  const contextValue = useMemo(
    () => ({
      isLoading,
      showLoader,
      hideLoader,
      disableGlobalOverlay,
      enableGlobalOverlay,
    }),
    [isLoading, showLoader, hideLoader, disableGlobalOverlay, enableGlobalOverlay]
  );

  return (
    <GlobalLoaderContext.Provider value={contextValue}>
      {children}
      <GlobalLoadingOverlay isLoading={isLoading} />
    </GlobalLoaderContext.Provider>
  );
}

export function useGlobalLoader() {
  return useContext(GlobalLoaderContext);
}

function GlobalLoadingOverlay({ isLoading }) {
  if (!isLoading) return null;

  return (
    <div
      className="fixed inset-0 z-[900] flex items-center justify-center bg-black bg-opacity-40"
      aria-live="polite"
      aria-label="Loading"
    >
      <div className="flex flex-col items-center gap-3 p-4 bg-white/95 rounded-xl shadow-lg">
        <svg
          className="animate-spin h-10 w-10 text-primary"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          />
        </svg>
        <span className="text-sm font-semibold text-slate-700">One moment, loading …</span>
      </div>
    </div>
  );
}
